import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Header";
import TeamsTab from "@/pages/admin/TeamsTab";
import RosterTab from "@/pages/admin/RosterTab";
import GamesTab from "@/pages/admin/GamesTab";
import AnnouncementTab from "@/pages/admin/AnnouncementTab";
import RegistrationsTab from "@/pages/admin/RegistrationsTab";
import OfficialsTab from "@/pages/admin/OfficialsTab";
import UsersTab from "@/pages/admin/UsersTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SponsorsTab from "./SponsorsTab";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState(() => localStorage.getItem("ykpbl-admin-tab") || "teams");

    useEffect(() => {
        if (user === false) navigate("/admin/login", { replace: true });
    }, [user, navigate]);

    if (!user)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#08080C]">
                <Loader2 className="w-8 h-8 text-[#FF5500] animate-spin" data-testid="admin-loading" />
            </div>
        );

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#08080C]">
            <header className="sticky top-0 z-50 bg-[#08080C]/85 backdrop-blur-xl border-b border-[#262638]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo />
                        <span className="hidden sm:inline-block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FFB800] border border-[#FFB800]/40 rounded-full px-3 py-1">
                            Admin Console
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden md:block text-xs text-slate-400 font-stat">{user.username || user.email}</span>
                        <button
                            data-testid="admin-logout-button"
                            onClick={async () => {
                                await logout();
                                navigate("/");
                            }}
                            className="flex items-center gap-2 border border-[#262638] hover:border-[#EF4444] hover:text-[#EF4444] text-slate-300 font-display font-bold uppercase tracking-widest text-xs px-4 py-2 rounded-full transition-colors duration-200"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
                <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-white">
                    League Control Room
                </h1>
                <p className="text-sm text-slate-400 mt-2 mb-8">
                    Create teams, build rosters, schedule fixtures and enter results — standings update automatically.
                </p>

                <Tabs value={tab} onValueChange={(v) => { setTab(v); localStorage.setItem("ykpbl-admin-tab", v); }}>
                    <TabsList className="bg-[#12121A] border border-[#262638] flex-wrap h-auto">
                        <TabsTrigger data-testid="admin-tab-teams" value="teams" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Teams
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-roster" value="roster" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Rosters
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-games" value="games" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Schedule & Results
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-announcement" value="announcement" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Announcement
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-registrations" value="registrations" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Registrations
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-officials" value="officials" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Officials
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-users" value="users" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Users
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-sponsors" value="sponsors" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                            Sponsors
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="teams" className="mt-8">
                        <TeamsTab />
                    </TabsContent>
                    <TabsContent value="roster" className="mt-8">
                        <RosterTab />
                    </TabsContent>
                    <TabsContent value="games" className="mt-8">
                        <GamesTab />
                    </TabsContent>
                    <TabsContent value="announcement" className="mt-8">
                        <AnnouncementTab />
                    </TabsContent>
                    <TabsContent value="registrations" className="mt-8">
                        <RegistrationsTab />
                    </TabsContent>
                    <TabsContent value="officials" className="mt-8">
                        <OfficialsTab />
                    </TabsContent>
                    <TabsContent value="users" className="mt-8">
                        <UsersTab />
                    </TabsContent>
                    <TabsContent value="sponsors" className="mt-8">
                        <SponsorsTab />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
